import { Form } from "@unform/web";
import {
  Calendar,
  GraduationCap,
  GitFork,
  Trash,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { isVisibleStudentGroup, studentGroupStatus } from "~/functions/studentGroupStatus";
import { Zoom, toast } from "react-toastify";
import createSocketToToast from "~/components/SocketToToast";
import useSocketStore from "~/store/socketStore";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { getRegistries, getTabsPermissions, tabAllowed } from "~/functions";
import { format, parseISO } from "date-fns";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import { FullGridContext } from "../..";
import FindGeneric from "~/components/Finds/FindGeneric";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import {
  classroomStatusOptions,
  studentgroupStatusOptions,
  yesOrNoOptions,
} from "~/functions/selectPopoverOptions";
import CheckboxInput from "~/components/RegisterForm/CheckboxInput";
import { pdfjs } from "react-pdf";

import { Document, Page } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
const ScopeSequenceViewer = ({ url }) => {
  const [numPages, setNumPages] = useState(0);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scale, setScale] = useState(3.75);

  useEffect(() => {
    function updateWidth() {
      if (!containerRef.current) return;
      setContainerWidth(Math.max(320, containerRef.current.clientWidth));
    }
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full max-h-[80vh] overflow-y-auto border rounded-md bg-white p-2"
    >
      <Document
        file={{ url }}
        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
        onLoadError={(err) => console.error("PDF load error:", err)}
        options={{
          cMapUrl: "/cmaps/",
          standardFontDataUrl: "/standard_fonts/",
        }}
      >
        <div className="flex flex-col gap-4 pb-4">
          <div className="flex items-center gap-4 px-2 py-2 sticky top-0 bg-white z-10 border-b">
            <button
              type="button"
              className="text-xs font-semibold px-3 py-1 rounded border hover:bg-gray-100"
              onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
            >
              Zoom -
            </button>
            <span className="text-xs font-semibold text-gray-700">
              Zoom: {scale.toFixed(2)}x
            </span>
            <button
              type="button"
              className="text-xs font-semibold px-3 py-1 rounded border hover:bg-gray-100"
              onClick={() => setScale((s) => Math.min(4, s + 0.25))}
            >
              Zoom +
            </button>
            <button
              type="button"
              className="text-xs font-semibold px-3 py-1 rounded border hover:bg-gray-100 ml-auto flex items-center gap-1"
              onClick={() => window.open(url, "_blank")}
            >
              Download PDF
            </button>
          </div>
          {Array.from({ length: numPages }, (_, idx) => (
            <Page
              key={`scope-page-${idx + 1}`}
              pageNumber={idx + 1}
              width={containerWidth}
              scale={scale}
              loading={
                <div className="flex h-48 w-full items-center justify-center">
                  Loading...
                </div>
              }
            />
          ))}
        </div>
      </Document>
    </div>
  );
};

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const { handleOpened, successfullyUpdated, setSuccessfullyUpdated } =
    useContext(FullGridContext);

  const tabsPermissions = getTabsPermissions("studentgroups", FullGridContext);
  const { accessModule } = useContext(FullGridContext);
  const splitAccess = accessModule?.children
    ?.find((el) => el.alias === "studentgroups")
    ?.children?.find((el) => el.alias === "split_acess")
    ?.MenuHierarchyXGroup?.view ?? false;

  const [pageData, setPageData] = useState({
    name: "",
    status: "In Formation",
    private: false,
    level: null,
    languagemode: null,
    classroom: null,
    workload: null,
    staff: null,
    students: [],
    studentxgroups: [],
    start_date: null,
    end_date: null,
    classes: [],
  });
  const [loading, setLoading] = useState(false);
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  const auth = useSelector((state) => state.auth);
  const [returnToWorkload, setReturnToWorkload] = useState({
    level_id: null,
    languagemode_id: null,
  });
  const [filter, setFilter] = useState(null);
  const [splitData, setSplitData] = useState({
    name: "",
    staff: null,
    classroom: null,
    selectedStudentIds: [],
  });
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitProgress, setSplitProgress] = useState(0);
  const [attendanceAudit, setAttendanceAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [repairLoading, setRepairLoading] = useState(false);
  const { socket } = useSocketStore();

  useEffect(() => {
    document.getElementById("scrollPage").scrollTo(0, 0);
  }, [activeMenu]);

  useEffect(() => {
    if (
      activeMenu !== "students" ||
      id === "new" ||
      !pageData.loaded ||
      pageData.status === "In Formation"
    ) {
      setAttendanceAudit(null);
      return;
    }
    loadAttendanceAudit();
  }, [activeMenu, id, pageData.loaded, pageData.status]);

  async function loadAttendanceAudit() {
    if (!pageData.loaded || pageData.status === "In Formation") {
      setAttendanceAudit(null);
      return;
    }
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/studentgroups/attendance-audit/${id}`);
      setAttendanceAudit(data);
    } catch (err) {
      setAttendanceAudit(null);
    }
    setAuditLoading(false);
  }

  async function handleAttendanceRepair(student_ids = null) {
    if (pageData.status === "In Formation") {
      return;
    }
    setRepairLoading(true);
    try {
      const { data } = await api.post(
        `/studentgroups/attendance-repair/${id}`,
        student_ids ? { student_ids } : {}
      );
      const created = data.repaired.reduce(
        (acc, student) => acc + student.created,
        0
      );
      if (data.still_incomplete.length > 0) {
        toast(
          `${created} attendances created, but ${data.still_incomplete.length} student(s) are still incomplete. Try again.`,
          { type: "warning", autoClose: 6000 }
        );
      } else {
        toast(`${created} attendances created.`, { autoClose: 3000 });
      }
      await loadAttendanceAudit();
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    }
    setRepairLoading(false);
  }

  function auditForStudent(student_id) {
    if (pageData.status === "In Formation" || !attendanceAudit?.students) {
      return null;
    }
    return attendanceAudit.students.find(
      (student) => student.student_id === student_id
    );
  }

  useEffect(() => {
    async function getPageData() {
      if (id !== "new") {
        try {
          const { data } = await api.get(`/studentgroups/${id}`);
          setPageData({
            ...data,
            loaded: true,
          });
          setReturnToWorkload({
            level_id: data.level?.id,
            languagemode_id: data.languagemode?.id,
          });
          const {
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          } = data;
          const registries = await getRegistries({
            created_by,
            created_at,
            updated_by,
            updated_at,
            canceled_by,
            canceled_at,
          });
          setRegistry(registries);
        } catch (err) {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        }
      } else {
        setPageData({ ...pageData, loaded: true });
        setFormType("full");
      }
    }
    getPageData();
  }, []);

  async function handleGeneralFormSubmit(data) {
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }
    setLoading(true);
    if (id === "new") {
      try {
        delete data.id;
        await api.post(`/studentgroups`, {
          ...data,
        });
        setLoading(false);
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        setLoading(false);
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    } else if (id !== "new") {
      try {
        await api.put(`/studentgroups/${id}`, {
          ...data,
        });
        setLoading(false);
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        setLoading(false);
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  async function handleSplit() {
    if (!splitData.name) {
      toast("Name is required.", { type: "error", autoClose: 3000 });
      return;
    }
    if (!splitData.staff?.id) {
      toast("Teacher is required.", { type: "error", autoClose: 3000 });
      return;
    }
    if (!splitData.classroom?.id) {
      toast("Classroom is required.", { type: "error", autoClose: 3000 });
      return;
    }
    if (splitData.selectedStudentIds.length === 0) {
      toast("Select at least one student.", { type: "error", autoClose: 3000 });
      return;
    }
    setSplitLoading(true);
    setSplitProgress(0);
    try {
      createSocketToToast(socket, "progresso_split", "Split", setSplitProgress);
      await api.post(`/studentgroups/${id}/split`, {
        name: splitData.name,
        staff_id: splitData.staff.id,
        classroom_id: splitData.classroom.id,
        student_ids: splitData.selectedStudentIds,
        socketId: socket.id,
      });
      setSplitLoading(false);
      setSplitProgress(0);
      handleOpened(null);
    } catch (err) {
      setSplitLoading(false);
      setSplitProgress(0);
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    }
  }

  function toggleSplitStudent(studentId) {
    setSplitData((prev) => {
      const already = prev.selectedStudentIds.includes(studentId);
      return {
        ...prev,
        selectedStudentIds: already
          ? prev.selectedStudentIds.filter((sid) => sid !== studentId)
          : [...prev.selectedStudentIds, studentId],
      };
    });
  }

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <GraduationCap size={16} /> Student Group
            </RegisterFormMenu>
            {id !== "new" && (
              <>
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="students"
                  messageOnDisabled="Create the student group to have access to students."
                >
                  <GraduationCap size={16} /> Students
                </RegisterFormMenu>
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="Class Planning"
                  disabled={!tabAllowed(tabsPermissions, "class-planning-tab")}
                  messageOnDisabled="You don't have permission to access this tab"
                >
                  <Calendar size={16} /> Class Planning
                </RegisterFormMenu>
                <RegisterFormMenu
                  setActiveMenu={setActiveMenu}
                  activeMenu={activeMenu}
                  name="Scope and Sequence"
                  disabled={
                    !tabAllowed(tabsPermissions, "scope-and-sequence-tab")
                  }
                  messageOnDisabled="You don't have permission to access this tab"
                >
                  <Calendar size={16} /> Scope & Sequence
                </RegisterFormMenu>
                {pageData.status === "Ongoing" && splitAccess && (
                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="Split"
                  >
                    <GitFork size={16} /> Split
                  </RegisterFormMenu>
                )}
              </>
            )}
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <InputContext.Provider
              value={{
                id,
                generalForm,
                setSuccessfullyUpdated,
                fullscreen,
                setFullscreen,
                successfullyUpdated,
                handleCloseForm,
                handleInactivate: () => null,
                canceled: pageData.canceled_at,
              }}
            >
              <div
                id="scrollPage"
                className="flex flex-col items-start justify-start text-sm overflow-y-scroll"
              >
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  {pageData.loaded ? (
                    <>
                      <FormHeader
                        access={access}
                        title={`Student Group: ${pageData.name || "new"}`}
                        registry={registry}
                        InputContext={InputContext}
                      />
                      <InputLineGroup
                        title="general"
                        activeMenu={activeMenu === "general"}
                      >
                        <FindGeneric
                          route="filials"
                          title="Filial"
                          scope="filial"
                          required
                          readOnly={
                            id !== "new" && pageData.status !== "In Formation"
                          }
                          InputContext={InputContext}
                          defaultValue={
                            id === "new" && auth.filial.id !== 1
                              ? {
                                  id: auth.filial.id,
                                  name: auth.filial.name,
                                }
                              : {
                                  id: pageData.filial?.id,
                                  name: pageData.filial?.name,
                                }
                          }
                          fields={[
                            {
                              title: "Name",
                              name: "name",
                            },
                          ]}
                        />
                        <InputLine title="General Data">
                          <Input
                            type="text"
                            name="name"
                            required
                            readOnly={!access.MenuHierarchyXGroup?.edit}
                            grow
                            title="Name"
                            defaultValue={pageData.name}
                            InputContext={InputContext}
                          />
                          <SelectPopover
                            name="status"
                            required
                            grow
                            title="Status"
                            readOnly
                            isSearchable
                            defaultValue={
                              studentgroupStatusOptions.find(
                                (opt) => opt.value === pageData.status
                              ) ||
                              studentgroupStatusOptions.find(
                                (opt) => opt.value === "In Formation"
                              )
                            }
                            options={studentgroupStatusOptions}
                            InputContext={InputContext}
                          />
                          <SelectPopover
                            name="private"
                            required
                            grow
                            title="Private"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable
                            defaultValue={yesOrNoOptions.find(
                              (type) => type.value === pageData.private
                            )}
                            InputContext={InputContext}
                          />
                        </InputLine>
                        <InputLine>
                          <Input
                            type="date"
                            name="start_date"
                            required
                            grow
                            title="Start Date"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            defaultValue={
                              pageData?.start_date
                                ? format(
                                    parseISO(pageData.start_date),
                                    "yyyy-MM-dd"
                                  )
                                : ""
                            }
                            InputContext={InputContext}
                          />
                          <Input
                            type="date"
                            name="end_date"
                            readOnly
                            grow
                            title="End Date"
                            defaultValue={
                              pageData?.end_date
                                ? format(
                                    parseISO(pageData.end_date),
                                    "yyyy-MM-dd"
                                  )
                                : ""
                            }
                            InputContext={InputContext}
                          />
                        </InputLine>
                        <FindGeneric
                          route="levels"
                          title="Level"
                          scope="level"
                          readOnly={
                            id !== "new" && pageData.status !== "In Formation"
                          }
                          required
                          InputContext={InputContext}
                          defaultValue={{
                            id: pageData.level?.id,
                            name: pageData.level?.name,
                            Programcategory:
                              pageData.level?.Programcategory?.name,
                          }}
                          setReturnFindGeneric={(level) =>
                            setReturnToWorkload({
                              ...returnToWorkload,
                              level_id: level.id,
                            })
                          }
                          fields={[
                            {
                              title: "Name",
                              name: "name",
                            },
                            {
                              title: "Program Category",
                              name: "name",
                              model: "Programcategory",
                            },
                          ]}
                        />
                        <FindGeneric
                          route="languagemodes"
                          title="Language Mode"
                          scope="languagemode"
                          readOnly={
                            id !== "new" && pageData.status !== "In Formation"
                          }
                          required
                          InputContext={InputContext}
                          defaultValue={{
                            id: pageData.languagemode?.id,
                            name: pageData.languagemode?.name,
                          }}
                          setReturnFindGeneric={(level) =>
                            setReturnToWorkload({
                              ...returnToWorkload,
                              languagemode_id: level.id,
                            })
                          }
                          fields={[
                            {
                              title: "Name",
                              name: "name",
                            },
                          ]}
                        />
                        <FindGeneric
                          route="workloads"
                          title="Workload"
                          scope="workload"
                          required
                          type={
                            returnToWorkload.level_id +
                            "," +
                            returnToWorkload.languagemode_id
                          }
                          InputContext={InputContext}
                          readOnly={
                            id !== "new" && pageData.status !== "In Formation"
                          }
                          defaultValue={{
                            id: pageData.workload?.id,
                            name: pageData.workload?.name,
                            Level: pageData.level?.name,
                            Languagemode: pageData.languagemode?.name,
                          }}
                          fields={[
                            {
                              title: "Name",
                              name: "name",
                            },
                            {
                              title: "Level",
                              name: "name",
                              model: "Level",
                            },
                            {
                              title: "Language Mode",
                              name: "name",
                              model: "Languagemode",
                            },
                          ]}
                        />
                        <InputLine title="Week days" left>
                          <CheckboxInput
                            name="monday"
                            shrink
                            title="Monday"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable
                            defaultValue={pageData.monday}
                            InputContext={InputContext}
                          />
                          <CheckboxInput
                            name="tuesday"
                            shrink
                            title="Tuesday"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.tuesday}
                            InputContext={InputContext}
                          />
                          <CheckboxInput
                            name="wednesday"
                            shrink
                            title="Wednesday"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.wednesday}
                            InputContext={InputContext}
                          />
                          <CheckboxInput
                            name="thursday"
                            shrink
                            title="Thursday"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.thursday}
                            InputContext={InputContext}
                          />
                          <CheckboxInput
                            name="friday"
                            shrink
                            title="Friday"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.friday}
                            InputContext={InputContext}
                          />
                          <CheckboxInput
                            name="saturday"
                            shrink
                            title="Saturday"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.saturday}
                            InputContext={InputContext}
                          />
                          <CheckboxInput
                            name="sunday"
                            shrink
                            title="Sunday"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.sunday}
                            InputContext={InputContext}
                          />
                        </InputLine>
                        <InputLine title="Shift" left>
                          <CheckboxInput
                            name="morning"
                            shrink
                            title="Morning"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.morning}
                            InputContext={InputContext}
                          />
                          <CheckboxInput
                            name="afternoon"
                            shrink
                            title="Afternoon"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.afternoon}
                            InputContext={InputContext}
                          />
                          <CheckboxInput
                            name="evening"
                            shrink
                            title="Evening"
                            readOnly={
                              id !== "new" && pageData.status !== "In Formation"
                            }
                            options={yesOrNoOptions}
                            isSearchable={false}
                            defaultValue={pageData.evening}
                            InputContext={InputContext}
                          />
                        </InputLine>
                        <FindGeneric
                          route="classrooms"
                          title="Classroom"
                          scope="classroom"
                          required
                          InputContext={InputContext}
                          type="Active"
                          readOnly={!access.MenuHierarchyXGroup?.edit}
                          defaultValue={{
                            id: pageData.classroom?.id,
                            class_number: pageData.classroom?.class_number,
                            quantity_of_students:
                              pageData.classroom?.quantity_of_students,
                          }}
                          fields={[
                            {
                              title: "Class Number",
                              name: "class_number",
                            },
                            {
                              title: "Quantity of Students",
                              name: "quantity_of_students",
                            },
                          ]}
                        />
                        <FindGeneric
                          route="staffs"
                          title="Teacher"
                          scope="staff"
                          required
                          readOnly={!access.MenuHierarchyXGroup?.edit}
                          type="Faculty"
                          InputContext={InputContext}
                          defaultValue={{
                            id: pageData.staff?.id,
                            name: pageData.staff?.name,
                            last_name: pageData.staff?.last_name,
                          }}
                          fields={[
                            {
                              title: "Name",
                              name: "name",
                            },
                            {
                              title: "Last Name",
                              name: "last_name",
                            },
                          ]}
                        />
                      </InputLineGroup>
                      <InputLineGroup
                        title="students"
                        activeMenu={activeMenu === "students"}
                      >
                        <div className="flex flex-row justify-evenly w-full items-center gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setFilter(
                                filter === "Terminated" ? null : "Terminated"
                              )
                            }
                            className={`${
                              filter === "Terminated"
                                ? "border border-dashed rounded-md border-gray-500 "
                                : ""
                            }`}
                          >
                            <div className="flex flex-row items-center rounded-md hover:bg-gray-50 justify-start gap-2 p-2">
                              <div className="w-4 h-4 border border-gray-500 bg-gray-200 rounded-xs"></div>
                              <div className="text-xs text-gray-400">
                                Inactive
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setFilter(
                                filter === "Transferred" ? null : "Transferred"
                              )
                            }
                            className={`${
                              filter === "Transferred"
                                ? "border border-dashed rounded-md border-sky-500 "
                                : ""
                            }`}
                          >
                            <div className="flex flex-row items-center rounded-md hover:bg-sky-50 justify-start gap-2 p-2">
                              <div className="w-4 h-4 border border-gray-500 bg-sky-200 rounded-xs"></div>
                              <div className="text-xs text-sky-400">
                                Transferred
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setFilter(
                                filter === "Not started" ? null : "Not started"
                              )
                            }
                            className={`${
                              filter === "Not started"
                                ? "border border-dashed rounded-md border-gray-500 "
                                : ""
                            }`}
                          >
                            <div className="flex flex-row items-center rounded-md hover:bg-gray-50 justify-start gap-2 p-2">
                              <div className="w-4 h-4 border border-gray-500 bg-amber-50 rounded-xs"></div>
                              <div className="text-xs text-amber-700">
                                Not started yet
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setFilter(filter === "Active" ? null : "Active")
                            }
                            className={`${
                              filter === "Active"
                                ? "border border-dashed rounded-md border-gray-500 "
                                : ""
                            }`}
                          >
                            <div className="flex flex-row items-center rounded-md hover:bg-gray-50 justify-start gap-2 p-2">
                              <div className="w-4 h-4 border border-gray-500 bg-gray-50 rounded-xs"></div>
                              <div className="text-xs text-gray-600">
                                Active
                              </div>
                            </div>
                          </button>
                        </div>
                        {pageData.status !== "In Formation" &&
                          attendanceAudit &&
                          attendanceAudit.affected_students > 0 && (
                            <div className="flex flex-row items-center justify-between w-full gap-4 p-3 rounded-md border border-red-300 bg-red-50">
                              <div className="flex flex-row items-start gap-2">
                                <TriangleAlert
                                  size={18}
                                  className="text-red-600 shrink-0 mt-0.5"
                                />
                                <div className="flex flex-col">
                                  <div className="text-xs font-bold text-red-700">
                                    {attendanceAudit.affected_students}{" "}
                                    student(s) with missing attendances
                                  </div>
                                  <div className="text-xs text-red-600">
                                    {attendanceAudit.total_missing} attendance
                                    record(s) were not created. These students
                                    are active but will not show up on the
                                    attendance screen.
                                  </div>
                                </div>
                              </div>
                              <button
                                type="button"
                                disabled={repairLoading}
                                onClick={() => handleAttendanceRepair()}
                                className="flex flex-row items-center gap-2 px-3 py-2 rounded-md bg-red-600 text-white text-xs hover:bg-red-700 disabled:opacity-50 shrink-0"
                              >
                                <Wrench size={14} />
                                {repairLoading ? "Fixing..." : "Fix all"}
                              </button>
                            </div>
                          )}
                        <table className="w-full relative overflow-hidden">
                          <thead className="sticky top-0 border-b bg-white">
                            <tr>
                              <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-center w-8"></th>
                              <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-left">
                                Name
                              </th>
                              <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-left">
                                Last Name
                              </th>
                              <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-center">
                                Registration Number
                              </th>
                              <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-center">
                                Start Date
                              </th>
                              <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-center">
                                End Date
                              </th>
                              <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-center">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageData.studentxgroups &&
                              pageData.studentxgroups.length > 0 &&
                              pageData.studentxgroups
                                .filter(isVisibleStudentGroup)
                                .sort((a, b) =>
                                  a.student.name +
                                    a.student.last_name +
                                    a.start_date +
                                    a.end_date >
                                  b.student.name +
                                    b.student.last_name +
                                    b.start_date +
                                    b.end_date
                                    ? 1
                                    : -1
                                )
                                .filter((studentGroup) => {
                                  if (filter) {
                                    if (filter === "Active") {
                                      return studentGroupStatus(studentGroup) === "Active";
                                    } else if (filter === "Not started") {
                                      return studentGroupStatus(studentGroup) === "Not started";
                                    } else if (filter === "Terminated") {
                                      return studentGroupStatus(studentGroup) === "Inactive";
                                    } else if (filter === "Transferred") {
                                      return studentGroupStatus(studentGroup) === "Transferred";
                                    } else {
                                      return false;
                                    }
                                  }
                                  return true;
                                })
                                .map((studentGroup) => {
                                  const studentAudit = auditForStudent(
                                    studentGroup.student.id
                                  );
                                  const membershipStatus = studentGroupStatus(studentGroup);

                                  return (
                                    <tr
                                      key={studentGroup.id}
                                      className={`text-xs hover:bg-gray-50 border-b ${
                                        membershipStatus === "Not started"
                                          ? "bg-amber-50 text-amber-700"
                                          : membershipStatus === "Inactive"
                                          ? "bg-gray-100 text-gray-400"
                                          : membershipStatus === "Canceled"
                                          ? "bg-gray-100 text-gray-400"
                                          : membershipStatus === "Transferred"
                                          ? "bg-sky-50 text-sky-700"
                                          : membershipStatus === "Active"
                                          ? ""
                                          : ""
                                      }`}
                                    >
                                      <td className="text-xs px-1 py-2 text-center">
                                        {studentAudit &&
                                          studentAudit.missing > 0 && (
                                            <button
                                              type="button"
                                              disabled={repairLoading}
                                              title={`${studentAudit.missing} missing attendance(s) out of ${studentAudit.expected}. First missing class: ${studentAudit.missing_dates[0]}. Click to fix.`}
                                              onClick={() =>
                                                handleAttendanceRepair([
                                                  studentAudit.student_id,
                                                ])
                                              }
                                            >
                                              <TriangleAlert
                                                size={14}
                                                className="text-red-600 hover:text-red-800"
                                              />
                                            </button>
                                          )}
                                      </td>
                                      <td className="text-xs px-2 py-2">
                                        {studentGroup.student.name}
                                      </td>
                                      <td className="text-xs px-2 py-2">
                                        {studentGroup.student.last_name}
                                      </td>
                                      <td className="text-xs px-2 py-2 text-center">
                                        {
                                          studentGroup.student
                                            .registration_number
                                        }
                                      </td>
                                      <td className="text-xs px-2 py-2 text-center">
                                        {studentGroup.start_date &&
                                          format(
                                            parseISO(studentGroup.start_date),
                                            "MM/dd/yyyy"
                                          )}
                                      </td>
                                      <td className="text-xs px-1 py-2 text-center">
                                        {studentGroup.end_date &&
                                          format(
                                            parseISO(studentGroup.end_date),
                                            "MM/dd/yyyy"
                                          )}
                                      </td>
                                      <td className="text-xs px-1 py-2 text-center">
                                        {membershipStatus}
                                      </td>
                                    </tr>
                                  );
                                })}
                          </tbody>
                        </table>
                      </InputLineGroup>
                      <InputLineGroup
                        title="Class Planning"
                        activeMenu={activeMenu === "Class Planning"}
                      >
                        <table className="w-full relative overflow-hidden">
                          <thead className="sticky top-0 border-b bg-white">
                            <tr>
                              <th className="text-xs max-w-8 w-8 bg-white border rounded-sm p-2 hover:bg-gray-100 text-center">
                                #
                              </th>
                              <th className="text-xs max-w-10 w-10 bg-white border rounded-sm p-2 hover:bg-gray-100 text-center">
                                Date
                              </th>
                              <th className="text-xs bg-white border rounded-sm p-2 hover:bg-gray-100 text-left">
                                Notes
                              </th>
                              <th className="text-xs bg-white border rounded-sm p-2 hover:bg-gray-100 text-left">
                                Content Planned
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {pageData.classes.length > 0 &&
                              pageData.classes
                                .sort((a, b) => (a.date < b.date ? -1 : 1))
                                .map((classDate, index) => {
                                  return (
                                    <tr
                                      key={index}
                                      className="text-xs hover:bg-gray-50 border-b"
                                    >
                                      <td className="text-xs px-1 py-2">
                                        <div className="text-center">
                                          {index + 1}
                                        </div>
                                      </td>
                                      <td className="text-xs px-1 py-2 text-center">
                                        <strong className="text-sm">
                                          {classDate.date &&
                                            format(
                                              parseISO(classDate.date),
                                              "MM/dd"
                                            )}
                                        </strong>
                                        <br />
                                        {classDate.weekday}
                                      </td>
                                      <td className="text-xs px-1 py-2">
                                        {classDate.notes}
                                      </td>
                                      <td className="text-xs px-1 py-2">
                                        <div className="flex flex-row items-center justify-start gap-2">
                                          {classDate.paceguides.length > 0 &&
                                          classDate.paceguides[0].type ===
                                            "Review" ? (
                                            <span>
                                              ({classDate.paceguides[0].type})
                                            </span>
                                          ) : null}
                                          {classDate.paceguides &&
                                            classDate.paceguides
                                              .sort((a, b) =>
                                                a.description < b.description
                                                  ? -1
                                                  : 1
                                              )
                                              .map((paceGuide, index) => {
                                                return (
                                                  <div
                                                    key={index}
                                                    className="text-xs px-2 py-1 bg-gray-100 transition-all ease-in hover:bg-primary hover:text-white border rounded-md flex flex-row items-center justify-start gap-2"
                                                  >
                                                    {paceGuide.description}
                                                    {String(paceGuide.type || "").toUpperCase().includes("TEST") && (
                                                      <span className={`rounded px-1 py-0.5 font-semibold ${paceGuide.is_written ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                                                        {paceGuide.is_written ? "Written" : "Non-written"}
                                                      </span>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                          </tbody>
                        </table>
                      </InputLineGroup>
                      <InputLineGroup
                        title="Scope and Sequence"
                        activeMenu={activeMenu === "Scope and Sequence"}
                      >
                        <InputLine title="Scope and Sequence">
                          {pageData.workload?.File ? (
                            <ScopeSequenceViewer
                              url={pageData.workload?.File?.url}
                            />
                          ) : (
                            <p className="text-sm">No attached file.</p>
                          )}
                        </InputLine>
                      </InputLineGroup>
                      {pageData.status === "Ongoing" && splitAccess && (
                        <InputLineGroup
                          title="Split"
                          activeMenu={activeMenu === "Split"}
                        >
                          <InputLine title="New Student Group">
                            <div className="flex flex-col gap-1 w-full">
                              <label className="text-xs font-semibold text-gray-500 uppercase">
                                Name
                              </label>
                              <input
                                type="text"
                                className="border rounded-md px-3 py-2 text-sm w-full focus:outline-none focus:ring-1 focus:ring-primary"
                                placeholder="New student group name"
                                value={splitData.name}
                                onChange={(e) =>
                                  setSplitData((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </InputLine>
                          <InputLine title="Teacher &amp; Classroom">
                            <div className="flex flex-col gap-1 w-full">
                              <FindGeneric
                                route="classrooms"
                                title="Classroom"
                                scope="split_classroom"
                                InputContext={InputContext}
                                type="Active"
                                defaultValue={{
                                  id: splitData.classroom?.id,
                                  class_number: splitData.classroom?.class_number,
                                  quantity_of_students:
                                    splitData.classroom?.quantity_of_students,
                                }}
                                setReturnFindGeneric={(classroom) =>
                                  setSplitData((prev) => ({
                                    ...prev,
                                    classroom,
                                  }))
                                }
                                fields={[
                                  {
                                    title: "Class Number",
                                    name: "class_number",
                                  },
                                  {
                                    title: "Quantity of Students",
                                    name: "quantity_of_students",
                                  },
                                ]}
                              />
                              <FindGeneric
                                route="staffs"
                                title="Teacher"
                                scope="split_staff"
                                type="Faculty"
                                InputContext={InputContext}
                                defaultValue={{
                                  id: splitData.staff?.id,
                                  name: splitData.staff?.name,
                                  last_name: splitData.staff?.last_name,
                                }}
                                setReturnFindGeneric={(staff) =>
                                  setSplitData((prev) => ({
                                    ...prev,
                                    staff,
                                  }))
                                }
                                fields={[
                                  {
                                    title: "Name",
                                    name: "name",
                                  },
                                  {
                                    title: "Last Name",
                                    name: "last_name",
                                  },
                                ]}
                              />
                            </div>
                          </InputLine>
                          <InputLine title="Select Students">
                            <div className="flex flex-col w-full gap-2">
                              <table className="w-full relative overflow-hidden">
                                <thead className="sticky top-0 border-b bg-white">
                                  <tr>
                                    <th className="text-xs w-8 border rounded-sm p-2 text-center bg-white"></th>
                                    <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-left bg-white">
                                      Name
                                    </th>
                                    <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-left bg-white">
                                      Last Name
                                    </th>
                                    <th className="text-xs border rounded-sm p-2 hover:bg-gray-100 text-center bg-white">
                                      Registration Number
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(pageData.studentxgroups || [])
                                      .filter((sg) =>
                                        sg.start_date &&
                                        parseISO(sg.start_date) <= new Date() &&
                                        !sg.end_date
                                      )
                                      .sort((a, b) =>
                                        a.student.name + a.student.last_name >
                                        b.student.name + b.student.last_name
                                          ? 1
                                          : -1
                                      )
                                      .map((sg) => {
                                        const isSelected =
                                          splitData.selectedStudentIds.includes(
                                            sg.student.id
                                          );
                                        return (
                                          <tr
                                            key={sg.id}
                                            onClick={() =>
                                              toggleSplitStudent(sg.student.id)
                                            }
                                            className={`text-xs border-b cursor-pointer transition-colors ${
                                              isSelected
                                                ? "bg-orange-100 hover:bg-orange-200"
                                                : "hover:bg-gray-50"
                                            }`}
                                          >
                                            <td className="px-2 py-2 text-center">
                                              <input
                                                type="checkbox"
                                                readOnly
                                                checked={isSelected}
                                                className="accent-orange-500 cursor-pointer"
                                                onChange={() => {}}
                                              />
                                            </td>
                                            <td className="px-2 py-2">
                                              {sg.student.name}
                                            </td>
                                            <td className="px-2 py-2">
                                              {sg.student.last_name}
                                            </td>
                                            <td className="px-2 py-2 text-center">
                                              {sg.student.registration_number}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                </tbody>
                              </table>
                              <div className="flex flex-row justify-between items-center px-1">
                                <span className="text-xs text-gray-500">
                                  {splitData.selectedStudentIds.length} student
                                  {splitData.selectedStudentIds.length !== 1
                                    ? "s"
                                    : ""}{" "}
                                  selected
                                </span>
                                <button
                                  type="button"
                                  disabled={splitLoading}
                                  onClick={handleSplit}
                                  className="text-xs font-semibold px-4 py-2 rounded-md bg-primary text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
                                >
                                  {splitLoading ? (
                                    <span className="flex flex-row items-center gap-1">
                                      <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                      </svg>
                                      {splitProgress > 0 ? `${splitProgress}%` : "Processing..."}
                                    </span>
                                  ) : "Confirm Split"}
                                </button>
                              </div>
                            </div>
                          </InputLine>
                        </InputLineGroup>
                      )}
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </Form>
              </div>
            </InputContext.Provider>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
